import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, FileText, ArrowUpDown, Play } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useWorkTemplates, useDeleteWorkTemplate, useInstantiateWorkTemplate } from '@/hooks/use-work';
import { ProjectSearchComboBox } from '@/components/shared/project-search-combo-box';
import type { WorkStepTemplate } from '@/lib/types';
import type { ProjectOption } from '@/components/shared/project-search-combo-box';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

const PAGE_SIZE = 20;

export function WorkTemplatesPage() {
  const timeZone = useOrgTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [activeTemplate, setActiveTemplate] = useState<WorkStepTemplate | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const q = searchParams.get('q') || '';

  const { data, isLoading, isError, refetch } = useWorkTemplates({
    page,
    page_size: PAGE_SIZE,
    q,
    ordering: '-created_at',
  });
  const deleteMutation = useDeleteWorkTemplate();
  const instantiateMutation = useInstantiateWorkTemplate();

  const templates = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleInstantiate = async () => {
    if (!activeTemplate || !selectedProject) return;
    await instantiateMutation.mutateAsync({
      id: activeTemplate.id,
      data: { project: selectedProject.id },
    });
    setActiveTemplate(null);
    setSelectedProject(null);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Step Templates</h1>
          <p className="text-gray-600 mt-1">
            Reusable checklists that can be instantiated onto projects
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button asChild>
            <Link to="/work/templates/new">
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Input
              placeholder="Search templates..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="max-w-sm"
            />
            <Button variant="secondary" onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-red-600">Failed to load templates.</p>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <FileText className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No templates yet</h3>
          <p className="text-gray-500 mt-1">
            Create reusable step templates for recurring tasks.
          </p>
          <Button className="mt-4" asChild>
            <Link to="/work/templates/new">Create Template</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <Link
                        to={`/work/templates/${template.id}`}
                        className="font-medium hover:underline"
                      >
                        {template.name}
                      </Link>
                      {template.description && (
                        <p className="text-sm text-gray-500">{template.description}</p>
                      )}
                    </TableCell>
                    <TableCell>{template.items.length}</TableCell>
                    <TableCell>
                      {formatDateTimeInTimeZone(template.created_at, timeZone, {
                        dateStyle: 'medium',
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/work/templates/${template.id}`}>
                            <ArrowUpDown className="h-4 w-4 mr-1" />
                            Edit
                          </Link>
                        </Button>
                        <Dialog
                          open={activeTemplate?.id === template.id}
                          onOpenChange={(open) => {
                            if (!open) {
                              setActiveTemplate(null);
                              setSelectedProject(null);
                            }
                          }}
                        >
                          <DialogTrigger asChild>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setActiveTemplate(template);
                                setSelectedProject(null);
                              }}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Instantiate
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Instantiate on project</DialogTitle>
                              <DialogDescription>
                                Create a task from &quot;{template.name}&quot; on the selected
                                project.
                              </DialogDescription>
                            </DialogHeader>
                            <Label>Project *</Label>
                            <ProjectSearchComboBox
                              value={selectedProject}
                              onChange={setSelectedProject}
                              required
                            />
                            <DialogFooter>
                              <Button
                                data-testid="template-instantiate-confirm"
                                onClick={handleInstantiate}
                                disabled={instantiateMutation.isPending}
                              >
                                {instantiateMutation.isPending ? 'Creating...' : 'Instantiate'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => deleteMutation.mutate(template.id)}
                          disabled={deleteMutation.isPending}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                    className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      onClick={() => updateSearchParams({ page: String(p) })}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })
                    }
                    className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}
